import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../components/UserContext";
import axios from "axios";
import Cookies from "js-cookie";
import {
  BookingData,
  isUpcomingBooking,
  formatDateRange,
} from "../../../utils/bookingUtils";
import {
  BookingStatus,
  BookingType,
} from "../../../constants-generated/well-known-values";

interface BookingResponse {
  success: boolean;
  message: string;
  bookings: BookingData[];
}

interface HostDashboardPageProps {
  onNavigate?: (section: string) => void;
}

export default function HostDashboardPage({ onNavigate }: HostDashboardPageProps) {
  const { user } = useContext(UserContext);
  const [bookings, setBookings] = useState<BookingData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchBookings = async () => {
      const token = Cookies.get("authToken");
      try {
        const res = await axios.get<BookingResponse>(
          `/api/booking/list-workspace-owner?OwnerId=${user.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data.success) {
          setBookings(res.data.bookings);
        }
      } catch (err) {
        console.error("Error fetching bookings for dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [user]);

  // ── Metrics ────────────────────────────────────────────────────────────────
  const activeBookings = bookings.filter(
    (b) => b.status !== BookingStatus.Cancelled
  );
  const totalRevenue = activeBookings.reduce((s, b) => s + b.totalPrice, 0);
  const earned = bookings
    .filter((b) => b.status === BookingStatus.Completed)
    .reduce((s, b) => s + b.totalPrice, 0);
  const projected = bookings
    .filter(
      (b) =>
        b.status === BookingStatus.Confirmed && isUpcomingBooking(b.endDate)
    )
    .reduce((s, b) => s + b.totalPrice, 0);
  const pendingBookings = bookings.filter(
    (b) => b.status === BookingStatus.Pending
  );

  const grandTotal = bookings.reduce((s, b) => s + b.totalPrice, 0) || 1;

  const statusRevenue = {
    [BookingStatus.Completed]: bookings
      .filter((b) => b.status === BookingStatus.Completed)
      .reduce((s, b) => s + b.totalPrice, 0),
    [BookingStatus.Confirmed]: bookings
      .filter((b) => b.status === BookingStatus.Confirmed)
      .reduce((s, b) => s + b.totalPrice, 0),
    [BookingStatus.Pending]: bookings
      .filter((b) => b.status === BookingStatus.Pending)
      .reduce((s, b) => s + b.totalPrice, 0),
    [BookingStatus.Cancelled]: bookings
      .filter((b) => b.status === BookingStatus.Cancelled)
      .reduce((s, b) => s + b.totalPrice, 0),
  };

  // Revenue by workspace
  const workspaceRevMap: Record<
    number,
    { title: string; revenue: number }
  > = {};
  bookings.forEach((b) => {
    if (!workspaceRevMap[b.workspaceId]) {
      workspaceRevMap[b.workspaceId] = { title: b.workspaceTitle, revenue: 0 };
    }
    if (b.status !== BookingStatus.Cancelled) {
      workspaceRevMap[b.workspaceId].revenue += b.totalPrice;
    }
  });
  const workspaceRevArray = Object.entries(workspaceRevMap)
    .map(([id, d]) => ({ id: Number(id), ...d }))
    .sort((a, b) => b.revenue - a.revenue);
  const maxWsRevenue = workspaceRevArray[0]?.revenue || 1;

  // Booking type breakdown
  const reservations = bookings.filter(
    (b) => b.bookingType === BookingType.Reservation
  );
  const tours = bookings.filter((b) => b.bookingType === BookingType.Tour);

  const statusCounts = {
    [BookingStatus.Confirmed]: bookings.filter(
      (b) => b.status === BookingStatus.Confirmed
    ).length,
    [BookingStatus.Pending]: bookings.filter(
      (b) => b.status === BookingStatus.Pending
    ).length,
    [BookingStatus.Completed]: bookings.filter(
      (b) => b.status === BookingStatus.Completed
    ).length,
    [BookingStatus.Cancelled]: bookings.filter(
      (b) => b.status === BookingStatus.Cancelled
    ).length,
  };

  // Workspace performance table
  const workspacePerf = workspaceRevArray.map((w) => {
    const wbs = bookings.filter((b) => b.workspaceId === w.id);
    const wsEarned = wbs
      .filter((b) => b.status === BookingStatus.Completed)
      .reduce((s, b) => s + b.totalPrice, 0);
    const wsExpected = wbs
      .filter(
        (b) =>
          b.status === BookingStatus.Confirmed && isUpcomingBooking(b.endDate)
      )
      .reduce((s, b) => s + b.totalPrice, 0);
    const sorted = [...wbs].sort(
      (a, b) =>
        new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
    );
    return {
      id: w.id,
      title: w.title,
      bookingCount: wbs.filter((b) => b.bookingType === BookingType.Reservation)
        .length,
      tourCount: wbs.filter((b) => b.bookingType === BookingType.Tour).length,
      earned: wsEarned,
      expected: wsExpected,
      lastActivity: sorted[0]
        ? new Date(sorted[0].startDate).toLocaleDateString("en-AU", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })
        : "—",
    };
  });

  // Upcoming confirmed bookings
  const upcomingBookings = bookings
    .filter(
      (b) =>
        b.status === BookingStatus.Confirmed && isUpcomingBooking(b.endDate)
    )
    .sort(
      (a, b) =>
        new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    )
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-2 border-[var(--secondary)] border-t-transparent animate-spin" />
          <span className="text-sm text-gray-500">Loading dashboard…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8">
      {/* ── KPI Row ──────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Revenue"
          value={`$${totalRevenue.toLocaleString()}`}
          sub="All active bookings"
          color="blue"
        />
        <KpiCard
          label="Earned"
          value={`$${earned.toLocaleString()}`}
          sub="Completed bookings"
          color="green"
        />
        <KpiCard
          label="Projected"
          value={`$${projected.toLocaleString()}`}
          sub="Confirmed upcoming"
          color="purple"
        />
        <KpiCard
          label="Pending Decisions"
          value={`${pendingBookings.length}`}
          sub="Require your action"
          color="yellow"
        />
      </div>

      {/* ── Revenue Breakdown ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* By status */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Revenue by Status
          </h3>
          <div className="space-y-3">
            {[
              {
                label: "Completed",
                value: statusRevenue[BookingStatus.Completed],
                color: "bg-blue-500",
              },
              {
                label: "Confirmed",
                value: statusRevenue[BookingStatus.Confirmed],
                color: "bg-green-500",
              },
              {
                label: "Pending",
                value: statusRevenue[BookingStatus.Pending],
                color: "bg-yellow-400",
              },
              {
                label: "Cancelled",
                value: statusRevenue[BookingStatus.Cancelled],
                color: "bg-red-400",
              },
            ].map(({ label, value, color }) => (
              <div key={label}>
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>{label}</span>
                  <span>
                    ${value.toLocaleString()} (
                    {Math.round((value / grandTotal) * 100)}%)
                  </span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${color} rounded-full transition-all`}
                    style={{ width: `${(value / grandTotal) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* By workspace */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Revenue by Workspace
          </h3>
          {workspaceRevArray.length === 0 ? (
            <p className="text-sm text-gray-400">No revenue data yet.</p>
          ) : (
            <div className="space-y-3">
              {workspaceRevArray.map(({ id, title, revenue }) => (
                <div key={id}>
                  <div className="flex justify-between text-xs text-gray-600 mb-1">
                    <span className="truncate max-w-[65%]">{title}</span>
                    <span>${revenue.toLocaleString()}</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[var(--secondary)] rounded-full transition-all"
                      style={{ width: `${(revenue / maxWsRevenue) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Booking Breakdown ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Reservations vs Tours */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Booking Types
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-xs text-blue-600 font-medium mb-1">
                Reservations
              </p>
              <p className="text-2xl font-bold text-blue-900">
                {reservations.length}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                $
                {reservations
                  .reduce((s, b) => s + b.totalPrice, 0)
                  .toLocaleString()}{" "}
                total
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-xs text-purple-600 font-medium mb-1">Tours</p>
              <p className="text-2xl font-bold text-purple-900">
                {tours.length}
              </p>
              <p className="text-xs text-purple-600 mt-1">
                $
                {tours
                  .reduce((s, b) => s + b.totalPrice, 0)
                  .toLocaleString()}{" "}
                total
              </p>
            </div>
          </div>
        </div>

        {/* Status distribution */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Booking Status Distribution
          </h3>
          <div className="flex flex-wrap gap-3">
            <StatusPill
              label="Confirmed"
              count={statusCounts[BookingStatus.Confirmed]}
              color="bg-green-100 text-green-800"
            />
            <StatusPill
              label="Pending"
              count={statusCounts[BookingStatus.Pending]}
              color="bg-yellow-100 text-yellow-800"
            />
            <StatusPill
              label="Completed"
              count={statusCounts[BookingStatus.Completed]}
              color="bg-blue-100 text-blue-800"
            />
            <StatusPill
              label="Cancelled"
              count={statusCounts[BookingStatus.Cancelled]}
              color="bg-red-100 text-red-800"
            />
          </div>
        </div>
      </div>

      {/* ── Workspace Performance Table ──────────────────────────────────────── */}
      {workspacePerf.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-700">
              Workspace Performance
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Workspace
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Bookings
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tours
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Earned
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expected
                  </th>
                  <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Activity
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {workspacePerf.map((w) => (
                  <tr
                    key={w.id}
                    onClick={() => onNavigate?.("listings")}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 font-medium text-gray-900 max-w-[200px] truncate">
                      {w.title}
                    </td>
                    <td className="px-4 py-4 text-gray-600">{w.bookingCount}</td>
                    <td className="px-4 py-4 text-gray-600">{w.tourCount}</td>
                    <td className="px-4 py-4 font-medium text-green-600">
                      ${w.earned.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-gray-600">
                      ${w.expected.toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-gray-500 text-xs">
                      {w.lastActivity}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Upcoming Timeline ────────────────────────────────────────────────── */}
      {upcomingBookings.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">
            Upcoming Confirmed Bookings
          </h3>
          <div className="space-y-3">
            {upcomingBookings.map((b) => (
              <div
                key={b.id}
                className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg"
              >
                <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {b.workspaceTitle}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDateRange(b.startDate, b.endDate)}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-semibold text-gray-900">
                    ${b.totalPrice.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">User #{b.userId}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Empty state ──────────────────────────────────────────────────────── */}
      {bookings.length === 0 && (
        <div className="text-center py-12">
          <div className="bg-white rounded-xl border border-gray-200 p-12 max-w-lg mx-auto">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              No data yet
            </h3>
            <p className="text-gray-500 text-sm">
              Your dashboard will populate once you receive bookings.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  sub,
  color,
}: {
  label: string;
  value: string;
  sub: string;
  color: "blue" | "green" | "purple" | "yellow";
}) {
  const colorMap = {
    blue: "bg-blue-50 text-blue-700 border-blue-100",
    green: "bg-green-50 text-green-700 border-green-100",
    purple: "bg-purple-50 text-purple-700 border-purple-100",
    yellow: "bg-yellow-50 text-yellow-700 border-yellow-100",
  };
  return (
    <div className={`rounded-xl border p-5 ${colorMap[color]}`}>
      <p className="text-xs font-medium opacity-70 mb-1">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs opacity-60 mt-1">{sub}</p>
    </div>
  );
}

function StatusPill({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: string;
}) {
  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${color}`}
    >
      <span>{label}</span>
      <span className="font-bold">{count}</span>
    </div>
  );
}
